import { jsx, jsxs } from "react/jsx-runtime";
import dedent from "dedent";
import { useId } from "react";
import { useFallbackId } from "../../../hooks/useFallbackId.mjs";
import { Box } from "../../Box/Box.mjs";
import { FieldLabel } from "../../FieldLabel/FieldLabel.mjs";
import { FieldMessage } from "../../FieldMessage/FieldMessage.mjs";
import { Stack } from "../../Stack/Stack.mjs";
import { buildDataAttributes } from "../buildDataAttributes.mjs";
import { mergeIds } from "../mergeIds.mjs";
import { validateTabIndex } from "../validateTabIndex.mjs";
const FieldGroup = ({
  id,
  disabled,
  children,
  secondaryLabel,
  tertiaryLabel,
  description,
  message,
  reserveMessageSpace = false,
  messageSpace = "xsmall",
  tone,
  required,
  role,
  tabIndex,
  data,
  componentName,
  ...restProps
}) => {
  const resolvedId = useFallbackId(id);
  const labelId = useId();
  const messageId = useId();
  const descriptionId = useId();
  let ariaLabelledBy;
  let ariaLabel;
  if ("label" in restProps && restProps.label) {
    ariaLabelledBy = labelId;
  } else if ("aria-labelledby" in restProps && restProps["aria-labelledby"]) {
    ariaLabelledBy = restProps["aria-labelledby"];
  } else if ("aria-label" in restProps && restProps["aria-label"]) {
    ariaLabel = restProps["aria-label"];
  }
  if (process.env.NODE_ENV !== "production") {
    if ("label" in restProps && typeof restProps.label === "string" && restProps.label.trim().length === 0 && !("aria-label" in restProps) && !("aria-labelledby" in restProps)) {
      console.warn(
        dedent`
          The "label" prop is required as the accessible name for a ${componentName}.
          If you are labelling the ${componentName} with another element or using a non-visual label please provide the appropriate props, either "aria-label" or "aria-labelledby".

          See the ${componentName} documentation for more information: https://seek-oss.github.io/braid-design-system/components/${componentName}#indirect-or-hidden-field-labels
        `
      );
    }
    validateTabIndex(tabIndex);
  }
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "fieldset",
      disabled,
      id: resolvedId,
      role,
      "aria-labelledby": ariaLabelledBy,
      "aria-label": ariaLabel,
      "aria-required": required,
      ...buildDataAttributes({ data, validateRestProps: restProps }),
      children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        "label" in restProps && restProps.label || description ? /* @__PURE__ */ jsx(Box, { component: "legend", id: labelId, children: /* @__PURE__ */ jsx(
          FieldLabel,
          {
            htmlFor: false,
            label: "label" in restProps ? restProps.label : void 0,
            secondaryLabel,
            tertiaryLabel,
            disabled,
            description,
            descriptionId: description ? descriptionId : void 0
          }
        ) }) : null,
        /* @__PURE__ */ jsxs(Stack, { space: messageSpace, children: [
          children({
            disabled,
            tabIndex,
            "aria-describedby": mergeIds(
              message ? messageId : void 0,
              description ? descriptionId : void 0
            )
          }),
          message && !disabled || reserveMessageSpace ? /* @__PURE__ */ jsx(
            FieldMessage,
            {
              id: messageId,
              tone,
              disabled,
              message,
              reserveMessageSpace
            }
          ) : null
        ] })
      ] })
    }
  );
};
export {
  FieldGroup
};
